/* eslint-disable react/jsx-one-expression-per-line */
import { useQuery } from '@tanstack/react-query';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GerenciadorPrecos } from '../../../../../@types/GerenciadorPrecos';
import { RootState } from '../../../../../@types/RootState';
import { Competitors, LoadingSpinerArea } from '../../../../../components';
import { AlertWithTimestamp } from '../../../../../components/AlertWithTimestamp';
import { Exception } from '../../../../../data';
import { UPDATE_GERENCIADOR_DATAPOINT_BY_ID } from '../../../../../reducers/gerenciadorPrecos/datapoints';
import { selectorDataPointsExhibitionType } from '../../../../../reducers/gerenciadorPrecos/datapointExhibitionType';
import { selectorDisabledCompetitors } from '../../../../../reducers/gerenciadorPrecos/disabledCompetitors';
import { GetCompetitorsInfo, getCompetitorsInfo } from '../../services';
import ExpandedRowDtoList from './ExpandedRowDtoList';
import ExpandedRowResumeCard from './ExpandedRowResumeCard';

const MemoExpandedRowResumeCard = memo(ExpandedRowResumeCard);
const MemoExpandedRowDtoList = memo(ExpandedRowDtoList);

type RowData = GerenciadorPrecos.RowData;

export type ExpandedRowCompetitorsProps = {
  rowData: RowData;
};

const ExpandedRowCompetitors = ({ rowData }: ExpandedRowCompetitorsProps) => {
  const options = useSelector((state: RootState) => {
    return state.gerenciadorPrecosReducer.expandedRowKey.options;
  });

  const {
    productId,
    storeId,
    productFamilyId,
    productsToBePricedId,
    competitorDays = 7,
    unselectedCompetitors,
    clusterId,
    clusterRule,
    factorProductMaster,
  } = rowData;

  const dispatch = useDispatch();

  const disabledCompetitors = useSelector(selectorDisabledCompetitors);
  const datapointExhibitionType = useSelector(selectorDataPointsExhibitionType);

  const model: GetCompetitorsInfo.Params = useMemo(() => {
    const includeClusterParams = datapointExhibitionType === 'PRODUCT_CLUSTER' || datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY';
    const includeProductFamilyId = datapointExhibitionType === 'FAMILY' || datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY';
    const includeFactorProductMaster = datapointExhibitionType === 'FAMILY' || datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY';

    return {
      productId,
      storeId,
      productsToBePricedId,
      competitorDays,
      productFamilyId: includeProductFamilyId ? productFamilyId : null,
      competitorStoreId: disabledCompetitors[productsToBePricedId] || [],
      unselectedCompetitors,
      clusterId: includeClusterParams ? clusterId : undefined,
      clusterRule: includeClusterParams ? (clusterRule || undefined) : undefined,
      factorProductMaster: includeFactorProductMaster && factorProductMaster != null && factorProductMaster !== 0 ? factorProductMaster : undefined,
    };
  }, [
    productId,
    storeId,
    productsToBePricedId,
    competitorDays,
    productFamilyId,
    disabledCompetitors,
    unselectedCompetitors,
    clusterId,
    clusterRule,
    factorProductMaster,
    datapointExhibitionType,
  ]);

  const handleGetCompetitors = useCallback(async () => {
    try {
      const res = await getCompetitorsInfo(model);
      return res;
    } catch {
      const { message } = new Exception('Erro ao buscar informações dos concorrentes');
      AlertWithTimestamp({ message });
    }
  }, [model]);

  const handleSuccess = useCallback(
    (competitorsInfo: GetCompetitorsInfo.Result) => {
      const payload = {
        id: productsToBePricedId,
        data: { competitorsInfo },
      };
      dispatch(UPDATE_GERENCIADOR_DATAPOINT_BY_ID(payload));
    },
    [dispatch, productsToBePricedId],
  );

  const { data } = useQuery({
    queryKey: ['ipa/gerenciador/competitors-info', model],
    keepPreviousData: false,
    queryFn: handleGetCompetitors,
    onSuccess: handleSuccess,
    enabled: !!rowData.competitorsPrice,
  });

  const resumeCardData = useMemo(() => {
    return data?.competitor ?? data?.competitorInfoPanel;
  }, [data?.competitor, data?.competitorInfoPanel]);

  const hasCompetitorsDto = data?.competitorPriceDTO !== undefined && Array.isArray(data?.competitorPriceDTO) && data.competitorPriceDTO.length > 0;

  if (!options.includes('COMPETITORS') && !options.includes('OFFERPRICE_COMPETITORS')) return null;

  return (
    <Competitors>
      {!resumeCardData && <p className="competitor-empty">Informações de concorrência indisponíveis</p>}
      {!!resumeCardData && (
        <>
          <MemoExpandedRowResumeCard rowData={rowData} resumeCardData={resumeCardData} competitor={data?.competitor} />

          {hasCompetitorsDto && (
            <>
              <Competitors.Separator />
              <Competitors.Section>
                <Competitors.Header>
                  <Competitors.Icon icon="DescArrowForward" />
                  <Competitors.Title>Lojas</Competitors.Title>
                </Competitors.Header>
                <Competitors.Content>
                  <MemoExpandedRowDtoList rowData={rowData} />
                </Competitors.Content>
              </Competitors.Section>
            </>
          )}
        </>
      )}
      <LoadingSpinerArea area={`competitor-row-${rowData.storeId}-${rowData.productId}`} height="100%" size="md" />
    </Competitors>
  );
};

export default ExpandedRowCompetitors;
